import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { toast } from 'sonner';

const apiClient: AxiosInstance = axios.create({
  baseURL: '/api',
  withCredentials: true,
});

const rawClient: AxiosInstance = axios.create({
  baseURL: '/api',
  withCredentials: true,
});

let refreshPromise: Promise<{ accessToken: string; refreshToken: string }> | null = null;

function isTokenExpiringSoon(token: string, skewSeconds = 120): boolean {
  try {
    const [, payloadB64] = token.split('.');
    if (!payloadB64) return false;
    const payloadJson = atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(payloadJson) as { exp?: number };
    if (!payload.exp) return false;
    const now = Math.floor(Date.now() / 1000);
    return now >= payload.exp - skewSeconds;
  } catch {
    return false;
  }
}

async function refreshTokensIfPossible() {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) throw new Error('No refresh token available');

  if (!refreshPromise) {
    refreshPromise = rawClient
      .post('/auth/refresh', { refreshToken })
      .then((r) => r.data.data as { accessToken: string; refreshToken: string })
      .finally(() => {
        refreshPromise = null;
      });
  }

  const tokens = await refreshPromise;
  localStorage.setItem('accessToken', tokens.accessToken);
  localStorage.setItem('refreshToken', tokens.refreshToken);
  return tokens;
}

apiClient.interceptors.request.use(async (config) => {
  // Evita loop di refresh su login/refresh/logout
  const url = config.url || '';
  const isAuthCall = url.includes('/auth/login') || url.includes('/auth/refresh') || url.includes('/auth/logout');

  let token = localStorage.getItem('accessToken');

  // Refresh proattivo se il token sta per scadere (riduce 401 e logout improvvisi dopo inattività)
  if (!isAuthCall && token && isTokenExpiringSoon(token)) {
    try {
      const tokens = await refreshTokensIfPossible();
      token = tokens.accessToken;
    } catch {
      // Se il refresh fallisce, lasciamo proseguire: la response interceptor gestirà il 401.
    }
  }

  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for 401 errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = (error.config || {}) as AxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 503) {
      const msg =
        (error.response?.data?.message as string) ||
        'Servizio temporaneamente non disponibile. Riprova tra poco.';
      toast.error(msg);
      return Promise.reject(error);
    }

    if (error.response?.status === 423) {
      const msg =
        (error.response?.data?.message as string) ||
        'Modalità manutenzione: le modifiche non sono consentite per il tuo ruolo.';
      toast.error(msg);
      window.dispatchEvent(new CustomEvent('maintenance:blocked', { detail: { message: msg } }));
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          const tokens = await refreshTokensIfPossible();

          originalRequest.headers = originalRequest.headers || {};
          (originalRequest.headers as any).Authorization = `Bearer ${tokens.accessToken}`;

          return apiClient(originalRequest);
        } catch {
          // Fallito refresh → logout pulito sotto
        }
      }

      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export { apiClient };

export const whatsappApi = {
  getConversations: () => apiClient.get('/whatsapp/conversations'),
  getConversationMessages: (conversationId: string) =>
    apiClient.get(`/whatsapp/conversations/${conversationId}/messages`),
  sendMessage: (payload: { to: string; text: string }) =>
    apiClient.post('/whatsapp/send', payload),
};
